import { format } from 'date-fns';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import useApiCall from '../../../../hooks/useApiCall';
import useGetGeneralAppData from '../../../../hooks/useGetGeneralAppData';
import { useQuery } from '../../../../hooks/useQuery';
import ReclamationsService from '../../../../services/ReclamationsService';
import splitArray from '../../../../utils/splitArray';
import { MappedReclamationType, ReclamationsFromApi } from '../../types';

interface UseLoadReclamationsI {
  setCurrentPage: Dispatch<SetStateAction<number>>;
}

export default function useLoadReclamations({ setCurrentPage }: UseLoadReclamationsI) {
  const [isLoadLoading, setIsLoadLoading] = useState(false);
  const [doesListApiHasError, setDoesListApiHasError] = useState(false);

  const [fullReclamations, setFullReclamations] = useState<MappedReclamationType[]>([]);
  const [filteredReclamations, setFilteredReclamations] = useState<MappedReclamationType[]>([]);
  const [reclamationsSplitted, setReclamationsSplitted] = useState<MappedReclamationType[][]>([]);

  const { apiCall } = useApiCall();
  const { userCode, userProfile, companyCode } = useGetGeneralAppData();
  const query = useQuery();
  const month = query.get('month');
  const year = query.get('year');

  const loadReclamations = useCallback(async () => {
    await apiCall({
      apiToCall: ReclamationsService.listConsultsWithSomeReclamationAtPeriod,
      queryParams: { codUsu: userCode, perfilUsu: userProfile, codEmpresa: companyCode, mes: month ?? '', ano: year ?? '' },
      onStartLoad: () => setIsLoadLoading(true),
      onEndLoad: () => setIsLoadLoading(false),
      catchMessage: 'Não foi posível carregar as consultas com contestações. Por favor, tente novamente',
      catchAction: () => setDoesListApiHasError(true),
      actionAfterResponse: (response: ReclamationsFromApi) => {
        const { codigo, msg, reclamacoesAbertas } = response;
        if (!codigo) {
          toast.error(`Não foi possível carregar as consultas com contestações. Por favor, tente novamente (${msg})`);
          setDoesListApiHasError(true);
          return;
        }

        const reclamationsMapped = reclamacoesAbertas.map((rcl) => ({
          date: format(new Date(rcl.dataGeracao),  'dd/MM/yyyy \'às\' HH\'h\'mm\'m\''),
          consultCode: rcl.codConsulta,
          uf: rcl.ufRes,
          workCity: rcl.cidadeTrab,
          homeCity: rcl.cidadeRes,
          routeType: rcl.tipoRotas,
          employeeName: rcl.nomeFunc,
        }));

        setFullReclamations(reclamationsMapped);
        const splitedArray = splitArray(reclamationsMapped);
        setReclamationsSplitted(splitedArray);
        setFilteredReclamations(splitedArray[0]);
        setCurrentPage(0);

        setDoesListApiHasError(false);
      },
    });
  }, [apiCall, companyCode, month, setCurrentPage, userCode, userProfile, year]);

  useEffect(() => {
    loadReclamations();
  }, [loadReclamations]);

  const spendTimeOptions = [
    { value: null, label: 'Todas' },
    { value: 'oneHourOrLess', label: '-1 hora' },
    { value: 'lessThanTwoHours', label: 'Entre 1 e 2 horas' },
    { value: 'moreThanTwoHours', label: '+2 horas' },
  ];

  const typeOptions = [
    { value: null, label: 'Todas' },
    { value: 'default', label: 'Padrões' },
    { value: 'auto', label: 'Automáticas' },
  ];

  return {
    isLoadLoading,
    doesListApiHasError,
    fullReclamations,
    reclamationsSplitted,
    filteredReclamations,
    loadReclamations,
    setReclamationsSplitted,
    setFilteredReclamations,
    spendTimeOptions,
    typeOptions,
  };
}
